<template>
    <DForm @submit.prevent="submitForm">
        <div class="product-form">
            <DCard
                :title="__('products.products.form.headline.product')"
                padding
            >
                <DFormRow
                    v-if="productGroupsAndProducts.length > 0"
                    :label="__('products.products.form.product_copy')"
                    :error="errors.product_copy_id"
                >
                    <DSelectGroup
                        id="product_copy_id"
                        v-model="form.product_copy_id"
                        :option-groups="productGroupsAndProducts"
                        :placeholder="__('products.products.form.none')"
                        :is-valid="isFormFieldValid('product_copy_id')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="productGroupsAndProducts.length > 0"
                    :label="__('products.products.form.copy_product_plans')"
                    :error="errors.copy_product_plans"
                    :hint="__('products.products.form.help_text.copy_product_plans')"
                >
                    <DSwitch
                        id="copy-product-plans"
                        v-model="form.copy_product_plans"
                        color="success"
                        variant="3d"
                        :disabled="!form.product_copy_id"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.name')"
                    is-required
                    :error="errors.name"
                    :hint="__('products.products.form.help_text.name', {test: nameRoute})"
                >
                    <DInput
                        id="name"
                        v-model="form.name"
                        :placeholder="__('products.products.form.name')"
                        :is-valid="isFormFieldValid('name')"
                        maxlength="255"
                    />

                    <TranslationLink
                        :tenant-languages="tenantLanguages"
                        :route="$route('products.products.translations', {product: product?.id ?? 0})"
                        :default-tenant-language="defaultTenantLanguage"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.productGroup')"
                    is-required
                    :error="errors.product_group_id"
                >
                    <DSelect
                        id="product_group"
                        v-model="form.product_group_id"
                        :options="productGroups"
                        :placeholder="__('products.products.form.productGroup')"
                        :is-valid="isFormFieldValid('product_group_id')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.productCode')"
                    :error="errors.name_short"
                    :hint="__('products.products.form.help_text.code')"
                >
                    <DInput
                        id="name_short"
                        v-model="form.name_short"
                        :placeholder="__('products.products.form.productCode')"
                        :is-valid="isFormFieldValid('name_short')"
                        maxlength="35"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.retired')"
                    :error="errors.retired"
                    :hint="__('products.products.form.help_text.retired')"
                >
                    <DSwitch
                        id="switch-retired"
                        v-model="form.retired"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.up_and_downgrade_only')"
                    :error="errors.up_and_downgrade_only"
                    :hint="__('products.products.form.help_text.up_and_downgrade_only')"
                >
                    <DSwitch
                        id="switch-up-and-downgrade-only"
                        v-model="form.up_and_downgrade_only"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.service_request.label')"
                    :error="errors.service_request"
                    :hint="__('products.products.form.service_request.description')"
                >
                    <DSwitch
                        id="switch-service-request"
                        v-model="form.service_request"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.sub_type')"
                    :error="errors.product_sub_types"
                    :hint="__('products.products.form.help_text.subtype')"
                >
                    <DMultiSelect
                        id="invoicing_types"
                        :options="selectedProductSubTypesOptions()"
                        :selection="true"
                        selection-type="tags"
                        :search="true"
                        :text="__('products.products.form.sub_type_hint')"
                        @update="updateSelectedProductSubTypes"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.onehome_configuration')"
                    :error="errors.onehome_configuration"
                    :hint="__('products.products.form.help_text.one_home_configuration')"
                >
                    <DJsonTextarea
                        id="onehome_configuration"
                        v-model="form.onehome_configuration"
                        :placeholder="__('products.products.form.onehome_configuration')"
                        :is-valid="isFormFieldValid('onehome_configuration')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.headline.product_classes')"
                    :is-required="false"
                    :hint="__('products.products.form.help_text.classes')"
                >
                    <DMultiSelect
                        id="product_classes"
                        :options="selectedProductClassesOptions()"
                        :selection="true"
                        selection-type="tags"
                        :search="true"
                        @update="updateSelectedProductClasses"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.order')"
                padding
            >
                <DFormRow
                    :label="__('products.products.form.requires_domain.label')"
                    :error="errors.requires_domain"
                    :hint="__('products.products.form.requires_domain.description')"
                >
                    <DSelect
                        id="requires_domain"
                        v-model="form.requires_domain"
                        :options="requiresDomainOptions"
                        :is-valid="isFormFieldValid('requires_domain')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.requires_recurring_payment.label')"
                    :error="errors.requires_recurring_payment"
                    :hint="__('products.products.form.requires_recurring_payment.description')"
                >
                    <DSwitch
                        id="requires-recurring-payment"
                        v-model="form.requires_recurring_payment"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.terms_and_conditions_type.label')"
                    :error="errors.terms_and_conditions_type"
                    :hint="__('products.products.form.terms_and_conditions_type.description')"
                >
                    <DSelect
                        id="terms_and_conditions_type"
                        v-model="form.terms_and_conditions_type"
                        :options="termsAndConditionsTypes"
                        :is-valid="isFormFieldValid('terms_and_conditions_type')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="needsTermsAndConditionsLink"
                    :label="__('products.products.form.terms_and_conditions_link.label')"
                    :error="errors.terms_and_conditions_link"
                >
                    <DInput
                        id="terms_and_conditions_link"
                        v-model="form.terms_and_conditions_link"
                        :placeholder="__('products.products.form.terms_and_conditions_link.label')"
                        :is-valid="isFormFieldValid('terms_and_conditions_link')"
                        maxlength="255"
                    />
                </DFormRow>
                <DFormRow
                    v-if="needsTermsAndConditionsLink"
                    :label="__('products.products.form.terms_and_conditions_link_label.label')"
                    :error="errors.terms_and_conditions_link_label"
                    :hint="__('products.products.form.terms_and_conditions_link_label.description')"
                >
                    <DInput
                        id="terms_and_conditions_link_label"
                        v-model="form.terms_and_conditions_link_label"
                        :placeholder="__('products.products.form.terms_and_conditions_link_label.label')"
                        :is-valid="isFormFieldValid('terms_and_conditions_link_label')"
                    />
                    <TranslationLink
                        :tenant-languages="tenantLanguages"
                        :route="$route('products.products.translations', {product: product?.id ?? 0})"
                        :default-tenant-language="defaultTenantLanguage"
                    />
                </DFormRow>
                <DFormRow
                    v-if="needsTermsAndConditionsLink"
                    :label="__('products.products.form.terms_and_conditions_link_2.label')"
                    :error="errors.terms_and_conditions_link_2"
                >
                    <DInput
                        id="terms_and_conditions_link_2"
                        v-model="form.terms_and_conditions_link_2"
                        :placeholder="__('products.products.form.terms_and_conditions_link_2.label')"
                        :is-valid="isFormFieldValid('terms_and_conditions_link_2')"
                        maxlength="255"
                    />
                </DFormRow>
                <DFormRow
                    v-if="needsTermsAndConditionsLink"
                    :label="__('products.products.form.terms_and_conditions_link_label_2.label')"
                    :error="errors.terms_and_conditions_link_label_2"
                    :hint="__('products.products.form.terms_and_conditions_link_label_2.description')"
                >
                    <DInput
                        id="terms_and_conditions_link_label_2"
                        v-model="form.terms_and_conditions_link_label_2"
                        :placeholder="__('products.products.form.terms_and_conditions_link_label_2.label')"
                        :is-valid="isFormFieldValid('terms_and_conditions_link_label_2')"
                    />
                    <TranslationLink
                        :tenant-languages="tenantLanguages"
                        :route="$route('products.products.translations', {product: product?.id ?? 0})"
                        :default-tenant-language="defaultTenantLanguage"
                    />
                </DFormRow>
                <DFormRow
                    v-if="needsTermsAndConditionsLink"
                    :label="__('products.products.form.terms_and_conditions_link_3.label')"
                    :error="errors.terms_and_conditions_link_3"
                >
                    <DInput
                        id="terms_and_conditions_link_3"
                        v-model="form.terms_and_conditions_link_3"
                        :placeholder="__('products.products.form.terms_and_conditions_link_3.label')"
                        :is-valid="isFormFieldValid('terms_and_conditions_link_3')"
                        maxlength="255"
                    />
                </DFormRow>
                <DFormRow
                    v-if="needsTermsAndConditionsLink"
                    :label="__('products.products.form.terms_and_conditions_link_label_3.label')"
                    :error="errors.terms_and_conditions_link_label_3"
                    :hint="__('products.products.form.terms_and_conditions_link_label_3.description')"
                >
                    <DInput
                        id="terms_and_conditions_link_label_3"
                        v-model="form.terms_and_conditions_link_label_3"
                        :placeholder="__('products.products.form.terms_and_conditions_link_label_3.label')"
                        :is-valid="isFormFieldValid('terms_and_conditions_link_label_3')"
                    />
                    <TranslationLink
                        :tenant-languages="tenantLanguages"
                        :route="$route('products.products.translations', {product: product?.id ?? 0})"
                        :default-tenant-language="defaultTenantLanguage"
                    />
                </DFormRow>
                <DFormRow
                    v-if="needsTermsAndConditionsText"
                    :label="__('products.products.form.terms_and_conditions.label')"
                    :error="errors.terms_and_conditions"
                    :hint="__('products.products.form.terms_and_conditions.description')"
                >
                    <DFormTextarea
                        id="terms_and_conditions"
                        v-model="form.terms_and_conditions"
                        :placeholder="__('products.products.form.terms_and_conditions.label')"
                        rows="10"
                    />
                    <TranslationLink
                        :tenant-languages="tenantLanguages"
                        :route="$route('products.products.translations', {product: product?.id ?? 0})"
                        :default-tenant-language="defaultTenantLanguage"
                    />
                </DFormRow>
                <DFormRow
                    v-if="needsTermsAndConditionsText"
                    :label="__('products.products.form.terms_and_conditions_text_confirmation_enabled.label')"
                    :error="errors.terms_and_conditions_text_confirmation_enabled"
                    :hint="__('products.products.form.terms_and_conditions_text_confirmation_enabled.description')"
                >
                    <DSwitch
                        id="terms_and_conditions_text_confirmation_enabled"
                        v-model="form.terms_and_conditions_text_confirmation_enabled"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    v-if="needsTermsAndConditions"
                    :label="__('products.products.form.terms_and_conditions_required_for_transfer.label')"
                    :error="errors.terms_and_conditions_required_for_transfer"
                    :hint="__('products.products.form.terms_and_conditions_required_for_transfer.description')"
                >
                    <DSwitch
                        id="terms_and_conditions_required_for_transfer"
                        v-model="form.terms_and_conditions_required_for_transfer"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.fulfillment')"
                padding
            >
                <DFormRow
                    :label="__('products.products.form.fulfillmentStrategy')"
                    :error="errors.fulfillment_strategy_id"
                    :hint="__('products.products.form.fulfillmentStrategy_hint')"
                >
                    <DSelect
                        id="fulfillment_strategy"
                        v-model="form.fulfillment_strategy_id"
                        :options="fulfillmentStrategies"
                        :placeholder="__('products.products.form.fulfillmentStrategy')"
                        :is-valid="isFormFieldValid('fulfillment_strategy_id')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.termination_rule_id')"
                    :error="errors.termination_rule_id"
                    is-required
                    :hint="__('products.products.form.help_text.termination_ruleset', {link: terminationRoute})"
                >
                    <DSelect
                        id="termination_rule"
                        v-model="form.termination_rule_id"
                        :options="terminationRules"
                        :placeholder="__('products.products.form.termination_rule_id')"
                        :is-valid="isFormFieldValid('termination_rule_id')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="isInvoiceBeforeRenewalEnabled === false"
                    :label="__('products.products.form.lead_time')"
                    :error="errors.lead_time"
                    :hint="__('products.products.form.lead_time_helper_text')"
                >
                    <DInput
                        id="lead_time"
                        v-model="form.lead_time"
                        type="number"
                        :placeholder="__('products.products.form.lead_time')"
                        :is-valid="isFormFieldValid('lead_time')"
                        min="0"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.fulfillmentModule')"
                    :error="errors.fulfillment_module"
                    :hint="__('products.products.form.help_text.fulfillment_module')"
                >
                    <DFieldLoading
                        v-if="isLoadingFulfillmentGroup"
                        :is-loading="isLoadingFulfillmentGroup"
                    />
                    <DSelect
                        v-else
                        id="fulfillment_module"
                        v-model="form.fulfillment_module"
                        :options="fulfillmentModules"
                        :placeholder="__('products.products.form.fulfillmentModule')"
                        :is-valid="isFormFieldValid('fulfillment_module')"
                        :disabled="isLoadingFulfillmentGroup || isLoadingFulfillmentOptions"
                        @change="changeFulfillmentModule"
                    />
                </DFormRow>
                <DFormRow
                    v-if="fulfillmentAccountGroups.length > 0"
                    :label="__('products.products.form.fulfillmentGroup')"
                    :error="errors.fulfillment_account_group_id"
                >
                    <DFieldLoading
                        v-if="isLoadingFulfillmentOptions"
                        :is-loading="isLoadingFulfillmentOptions"
                    />
                    <DSelect
                        v-else
                        id="fulfillment_account_group"
                        v-model="form.fulfillment_account_group_id"
                        :options="fulfillmentAccountGroups"
                        :placeholder="__('products.products.form.fulfillmentGroup')"
                        :is-valid="isFormFieldValid('fulfillment_account_group_id')"
                        :disabled="isLoadingFulfillmentGroup || isLoadingFulfillmentOptions"
                        @change="changeFulfillmentAccountGroup"
                    />
                </DFormRow>
                <DFormRow
                    v-if="fulfillmentAccountGroupsError"
                    label=""
                >
                    <DAlert color="danger">
                        {{ fulfillmentAccountGroupsError }}
                    </DAlert>
                </DFormRow>
                <div v-if="configOptions.length > 0">
                    <hr>
                    <DRow>
                        <DCol class="mb-2">
                            <h2 class="h4 mb-0">
                                {{ __('products.products.form.config') }}
                            </h2>
                            <small class="text-muted">{{ __(`products.products.form.sorting.${configValuesSortingType}`) }}</small>
                        </DCol>
                    </DRow>
                    <DFormRow
                        v-for="(row, index) in configOptions"
                        v-show="row.visible"
                        :key="index"
                        :label="row.translated_name"
                        :is-required="row.required"
                        :hint="row.description"
                        :error="errors[`config_values.${row.name}.value`]"
                    >
                        <DSwitch
                            v-if="row.type === 'bool'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            color="success"
                            variant="3d"
                            :class="{'is-invalid': !!errors[`config_values.${row.name}.value`]}"
                        />
                        <DInput
                            v-if="row.type === 'string'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                        <DInput
                            v-if="row.type === 'integer'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                        <DInput
                            v-if="row.type === 'email'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                        <DFormTextarea
                            v-if="row.type === 'text' || row.type === 'array'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                            rows="8"
                        />
                        <DSelect
                            v-if="row.type === 'list'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :options="row.options"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                        <DSelect
                            v-if="row.type === 'api'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :options="apiOptions[row.name]"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                    </DFormRow>
                    <hr>
                </div>
                <DFormRow
                    :label="__('products.products.form.dnsTemplate')"
                    :error="errors.dns_template_id"
                    :hint="__('products.products.form.help_text.dns_template')"
                >
                    <DSelect
                        id="dns_template"
                        v-model="form.dns_template_id"
                        :options="dnsTemplates"
                        :placeholder="__('products.products.form.dnsTemplate')"
                        :is-valid="isFormFieldValid('dns_template_id')"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.upgrades')"
                padding
            >
                <DFormRow
                    v-if="selectedFulfillmentAccountBrand"
                    :label="__('products.products.form.brand')"
                >
                    <span>{{ selectedFulfillmentAccountBrand }}</span>
                </DFormRow>
                <ProductChangesSelection
                    :label="__('products.products.form.upgrades')"
                    :product-id="product.id ?? null"
                    :multiple="true"
                    :selected-products="form.selectedUpgrades"
                    :required="false"
                    :fulfillment-module="form.fulfillment_module"
                    :fulfillment-account-group-id="form.fulfillment_account_group_id"
                    :errors="errors"
                    @update="updateSelectedUpgrades"
                />
                <ProductChangesSelection
                    :label="__('products.products.form.downgrades')"
                    :product-id="product.id ?? null"
                    :multiple="true"
                    :selected-products="form.selectedDowngrades"
                    :required="false"
                    :fulfillment-module="form.fulfillment_module"
                    :fulfillment-account-group-id="form.fulfillment_account_group_id"
                    :errors="errors"
                    @update="updateSelectedDowngrades"
                />
                <DFormRow
                    :label="__('products.products.form.headline.addon_groups')"
                    :is-required="false"
                >
                    <DMultiSelect
                        id="addon_groups"
                        :options="selectedAddonGroupsOptions()"
                        :selection="true"
                        selection-type="tags"
                        :search="true"
                        @update="updateSelectedAddonGroups"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.addon_groups.length"
                    :label="__('products.products.form.headline.default_addons')"
                    :is-required="false"
                    :hint="__('products.products.form.default_addons_hint')"
                >
                    <DMultiSelect
                        id="default_addons"
                        :key="defaultAddonKey"
                        :options="defaultAddonsSelection"
                        :selection="true"
                        selection-type="tags"
                        :search="true"
                        @update="updateSelectedDefaultAddons"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.invoice')"
                padding
            >
                <DFormRow
                    :label="__('products.products.form.invoice_text')"
                    :error="errors.invoice_text"
                    :hint="__('products.products.form.invoice_text_helper_text')"
                >
                    <DFormTextarea
                        id="invoice_text"
                        v-model="form.invoice_text"
                        :placeholder="__('products.products.form.invoice_text')"
                        rows="5"
                    />
                    <TranslationLink
                        :tenant-languages="tenantLanguages"
                        :route="$route('products.products.translations', {product: product?.id ?? 0})"
                        :default-tenant-language="defaultTenantLanguage"
                    />
                </DFormRow>
                <DFormRow
                    v-if="userPermissions.includes('product.assign_cost_objects')"
                    :label="__('products.products.form.accountingProduct')"
                    is-required
                    :error="errors.accounting_product_id"
                    :hint="__('products.products.form.help_text.accounting_product')"
                >
                    <DSelect
                        id="accounting_product_id"
                        v-model="form.accounting_product_id"
                        :options="accountingProducts"
                        :placeholder="__('products.products.form.accountingProduct')"
                        :is-valid="isFormFieldValid('accounting_product_id')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="userPermissions.includes('product.assign_cost_objects')"
                    :label="__('products.products.form.intercompany_accounting_product')"
                    is-required
                    :error="errors.intercompany_accounting_product_id"
                    :hint="__('products.products.form.help_text.intercompany_accounting_product')"
                >
                    <DSelect
                        id="intercompany_accounting_product_id"
                        v-model="form.intercompany_accounting_product_id"
                        :options="intercompanyAccountingProducts"
                        :placeholder="__('products.products.form.intercompany_accounting_product')"
                        :is-valid="isFormFieldValid('intercompany_accounting_product_id')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.invoice_behaviour')"
                    :error="errors.invoice_behaviour"
                    :hint="__('products.products.form.invoice_behaviour_hint')"
                >
                    <DSelect
                        id="invoice_behaviour"
                        v-model="form.invoice_behaviour"
                        :options="invoiceBehaviours"
                        :placeholder="__('products.products.form.invoice_behaviour')"
                        :is-valid="isFormFieldValid('invoice_behaviour')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.disable_invoice_before_renewal.label')"
                    :error="errors.disable_invoice_before_renewal"
                    :hint="__('products.products.form.disable_invoice_before_renewal.description')"
                >
                    <DSwitch
                        id="disable-invoice-before-renewal"
                        v-model="form.disable_invoice_before_renewal"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.billing_type.label')"
                    :error="errors.billing_type"
                    :hint="__('products.products.form.billing_type.description')"
                >
                    <DSelect
                        id="billing_type"
                        v-model="form.billing_type"
                        :options="billingTypes"
                        :is-valid="isFormFieldValid('billing_type')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="$page.props.tenantSettings.automatic_price_adjustment === true"
                    :label="__('products.products.form.automatic_price_adjustment')"
                    :error="errors.automatic_price_adjustment"
                    :hint="__('products.products.form.help_text.automatic_price_adjustments', {link: priceAdjustmentRoute})"
                >
                    <DSwitch
                        id="switch-automatic-price-adjustment"
                        v-model="form.automatic_price_adjustment"
                        color="success"
                        variant="3d"
                        @update:checked="updateAutomaticPrice"
                    />
                </DFormRow>
                <DFormRow
                    v-if="$page.props.tenantSettings.automatic_price_adjustment === true && form.automatic_price_adjustment === true"
                    :label="__('products.products.form.maximum_annual_price')"
                    :error="errors.maximum_annual_price"
                    :hint="__('products.products.form.maximum_annual_price_explanation')"
                >
                    <DInput
                        id="max_annual_price"
                        v-model="form.maximum_annual_price"
                        :is-valid="isFormFieldValid('maximum_annual_price')"
                        type="number"
                        step="0.001"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.accounting_information')"
                    :error="errors.accounting_information"
                    :hint="__('products.products.form.help_text.additional_accounting_information')"
                >
                    <DInput
                        id="accounting_information"
                        v-model="form.accounting_information"
                        :placeholder="__('products.products.form.accounting_information')"
                        type="string"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.termination')"
                padding
            >
                <DFormRow
                    :label="__('products.products.form.termination_on_dunning')"
                    :error="errors.termination_on_dunning"
                    :hint="__('products.products.form.termination_on_dunning_explanation')"
                >
                    <DSwitch
                        id="switch-termination-on-dunning"
                        v-model="form.termination_on_dunning"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.winback_ticket_on_termination.label')"
                    :error="errors.winback_ticket_on_termination"
                    :hint="__('products.products.form.help_text.create_winback_ticket_on_termination', {link: winbackRoute})"
                >
                    <DSwitch
                        id="winback-ticket-on-termination"
                        v-model="form.winback_ticket_on_termination"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    v-if="isShortTerminationPeriodSet"
                    :label="__('products.products.form.excluded_customer_protection_law')"
                    :error="errors.excluded_customer_protection_law"
                >
                    <DSwitch
                        id="switch-excluded-customer-protection-law"
                        v-model="form.excluded_customer_protection_law"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.taxonomy.headline')"
                padding
            >
                <DFormRow
                    :label="__('products.taxonomy.product_division')"
                    :error="errors.taxonomy_product_division"
                >
                    <DSelect
                        id="taxonomy_product_division"
                        v-model="form.taxonomy_product_division"
                        :options="taxonomyDivisionOptions"
                        :placeholder="__('messages.select_value')"
                        :is-valid="isFormFieldValid('taxonomy_product_division')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.taxonomy.product_category')"
                    :error="errors.taxonomy_product_category"
                >
                    <DSelect
                        id="taxonomy_product_category"
                        v-model="form.taxonomy_product_category"
                        :options="availableTaxonomyCategoryOptions"
                        :placeholder="__('messages.select_value')"
                        :is-valid="isFormFieldValid('taxonomy_product_category')"
                        :disabled="!form.taxonomy_product_division"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.taxonomy.product_subcategory')"
                    :error="errors.taxonomy_product_subcategory"
                >
                    <DSelect
                        id="taxonomy_product_subcategory"
                        v-model="form.taxonomy_product_subcategory"
                        :options="availableTaxonomySubcategoryOptions"
                        :placeholder="__('messages.select_value')"
                        :is-valid="isFormFieldValid('taxonomy_product_subcategory')"
                        :disabled="!form.taxonomy_product_category"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.taxonomy.product_item_name')"
                    :error="errors.taxonomy_product_item_name"
                >
                    <DInput
                        id="taxonomy_product_item_name"
                        v-model="form.taxonomy_product_item_name"
                        :placeholder="__('products.taxonomy.product_item_name')"
                        :is-valid="isFormFieldValid('taxonomy_product_item_name')"
                        maxlength="255"
                    />
                </DFormRow>
            </DCard>
        </div>
        <DModal
            v-if="showConfirmationModal"
            v-model:show="showConfirmationModal"
            :title="__('products.products.confirmation_modal.label')"
            :message="__('products.products.confirmation_modal.description', {listOfProducts: invalidProductUpgrades})"
            is-confirm
            @confirm="submitFormAfterConfirmation"
            @close="showConfirmationModal = false"
        />
        <DModal
            v-if="showRetiredWarningModal"
            v-model:show="showRetiredWarningModal"
            :title="__('products.products.retired_warning_modal.label')"
            :message="__('products.products.retired_warning_modal.description')"
            is-error
            is-confirm
            :confirm-button-text="'messages.save'"
            @confirm="submitFormAfterRetiredWarning"
            @close="showRetiredWarningModal = false"
        >
            <div class="retired-warning-list">
                <div v-if="retiredAddonNames.length">
                    <strong>{{ __('products.products.retired_warning_modal.addons_heading') }}</strong>
                    <ul>
                        <li
                            v-for="name in retiredAddonNames"
                            :key="name"
                        >
                            {{ name }}
                        </li>
                    </ul>
                </div>
                <div v-if="retiredAddonGroupNames.length">
                    <strong>{{ __('products.products.retired_warning_modal.addon_groups_heading') }}</strong>
                    <ul>
                        <li
                            v-for="name in retiredAddonGroupNames"
                            :key="name"
                        >
                            {{ name }}
                        </li>
                    </ul>
                </div>
            </div>
        </DModal>
        <DFormControls
            v-model="form.activity_log_comment"
            :cancel-route="$route('products.products.index')"
            :dirty="form.isDirty"
            :show-change-reason-input="true"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>

import ProductChangesSelection from '~@/Components/Products/Products/ProductChangesSelection'
import DFieldLoading from '~@/Components/Common/DFieldLoading'
import TranslationLink from '../../Settings/TranslationLink.vue'
import DSelectGroup from '~@/Components/Common/Forms/DSelectGroup.vue'

export default {
    name: 'ProductForm',
    components: {
        DSelectGroup,
        TranslationLink,
        DFieldLoading,
        ProductChangesSelection
    },
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        product: {
            type: Object,
            default: () => { return {} }
        },
        productGroupsAndProducts: {
            type: Array,
            default () {
                return []
            }
        },
        productGroups: {
            type: Array,
            default: () => { return [] }
        },
        dnsTemplates: {
            type: Array,
            default: () => { return [] }
        },
        accountingProducts: {
            type: Array,
            default: () => { return [] }
        },
        intercompanyAccountingProducts: {
            type: Array,
            default: () => { return [] }
        },
        fulfillmentStrategies: {
            type: Array,
            default: () => { return [] }
        },
        invoiceBehaviours: {
            type: Array,
            default: () => { return [] }
        },
        billingTypes: {
            type: Array,
            default: () => { return [] }
        },
        preselectedDowngrades: {
            type: Array,
            default: () => { return [] }
        },
        preselectedUpgrades: {
            type: Array,
            default: () => { return [] }
        },
        isShortTerminationPeriodSet: {
            type: Boolean,
            default: () => { return false }
        },
        subTypes: {
            type: Array,
            default () {
                return []
            }
        },
        defaultTenantLanguage: {
            type: String,
            default: ''
        },
        tenantLanguages: {
            type: Array,
            default: () => { return [] }
        },
        possibleAddonGroups: {
            type: Array,
            default: () => { return [] }
        },
        isInvoiceBeforeRenewalEnabled: {
            type: Boolean,
            default: false
        },
        productClasses: {
            type: Array,
            default: () => { return [] }
        },
        defaultAddons: {
            type: Array,
            default: () => { return [] }
        },
        terminationRules: {
            type: Array,
            default: () => { return [] }
        },
        termsAndConditionsTypes: {
            type: Array,
            default: () => { return [] }
        },
        requiresDomainOptions: {
            type: Array,
            default: () => { return [] }
        },
        taxonomyHierarchy: {
            type: Object,
            default: () => { return {} }
        },
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                name: (this.product?.name && this.productGroupsAndProducts.length > 0)
                    ? 'Copy of ' + this.product.name
                    : (this.product?.name ?? null),
                name_short: this.product?.name_short ?? null,
                invoice_text: this.product?.invoice_text ?? null,
                onehome_configuration: this.product?.onehome_configuration ?? null,
                product_group_id: this.product?.product_group_id ?? null,
                retired: this.product?.retired ?? false,
                up_and_downgrade_only: this.product?.up_and_downgrade_only ?? false,
                service_request: this.product?.service_request ?? false,
                invoice_behaviour: this.product?.invoice_behaviour ?? null,
                termination_on_dunning: this.product?.termination_on_dunning ?? true,
                winback_ticket_on_termination: this.product?.winback_ticket_on_termination ?? false,
                dns_template_id: this.product?.dns_template_id ?? null,
                accounting_product_id: this.product?.accounting_product_id ?? null,
                intercompany_accounting_product_id: this.product?.intercompany_accounting_product_id ?? null,
                fulfillment_strategy_id: this.product?.fulfillment_strategy_id ?? 'immediate',
                lead_time: this.product?.lead_time ?? 0,
                fulfillment_module: this.product?.fulfillment_module ?? null,
                fulfillment_account_group_id: this.product?.fulfillment_account_group_id ?? null,
                config_values: this.product?.config_values ?? [],
                selectedDowngrades: this.preselectedDowngrades ?? [],
                selectedUpgrades: this.preselectedUpgrades ?? [],
                default_addons: this.defaultAddons ?? [],
                excluded_customer_protection_law: this.product?.excluded_customer_protection_law ?? false,
                automatic_price_adjustment: this.product?.automatic_price_adjustment ?? false,
                maximum_annual_price: this.product?.maximum_annual_price ?? null,
                requires_domain: this.product?.requires_domain ?? 'never',
                disable_invoice_before_renewal: this.product?.disable_invoice_before_renewal ?? false,
                billing_type: this.product?.billing_type ?? null,
                accounting_information: this.product?.accounting_information ?? null,
                addon_groups: Array.isArray(this.product.product_addon_groups)
                    ? this.product.product_addon_groups.map((group) => group.product_group_id)
                    : [],
                is_create: Object.keys(this.product).length === 0 || this.productGroupsAndProducts.length > 0,
                activity_log_comment: null,
                product_copy_id: this.product?.id ?? null,
                copy_product_plans: false,
                termination_rule_id: this.product?.termination_rule_id ?? null,
                product_classes: Array.isArray(this.product.classes)
                    ? this.product.classes.map((productClass) => productClass.id)
                    : [],
                product_sub_types: Array.isArray(this.product.product_sub_types)
                    ? this.product.product_sub_types.map((productSubType) => productSubType.id)
                    : [],
                requires_recurring_payment: this.product?.requires_recurring_payment ?? false,
                terms_and_conditions_type: this.product?.terms_and_conditions_type ?? 'none',
                terms_and_conditions: this.product?.terms_and_conditions ?? null,
                terms_and_conditions_link: this.product?.terms_and_conditions_link ?? null,
                terms_and_conditions_link_label: this.product?.terms_and_conditions_link_label ?? null,
                terms_and_conditions_link_2: this.product?.terms_and_conditions_link_2 ?? null,
                terms_and_conditions_link_label_2: this.product?.terms_and_conditions_link_label_2 ?? null,
                terms_and_conditions_link_3: this.product?.terms_and_conditions_link_3 ?? null,
                terms_and_conditions_link_label_3: this.product?.terms_and_conditions_link_label_3 ?? null,
                terms_and_conditions_required_for_transfer: this.product?.terms_and_conditions_required_for_transfer ?? false,
                terms_and_conditions_text_confirmation_enabled: this.product?.terms_and_conditions_text_confirmation_enabled ?? true,
                taxonomy_product_division: this.product?.taxonomy_product_division ?? null,
                taxonomy_product_category: this.product?.taxonomy_product_category ?? null,
                taxonomy_product_subcategory: this.product?.taxonomy_product_subcategory ?? null,
                taxonomy_product_item_name: this.product?.taxonomy_product_item_name ?? null,
            }),
            configValuesSortingType: null,
            fulfillmentModules: [],
            fulfillmentAccountGroups: [],
            fulfillmentAccountGroupsError: null,
            configOptions: [],
            apiOptions: {},
            oldConfigOptions: {
                module: this.product?.fulfillment_module ?? null,
                values: this.product?.config_values ?? []
            },
            upgradeListKey: 0,
            isLoadingFulfillmentGroup: false,
            isLoadingFulfillmentOptions: false,
            invalidProductUpgrades: null,
            showConfirmationModal: false,
            showRetiredWarningModal: false,
            retiredAddonNames: [],
            retiredAddonGroupNames: [],
            defaultAddonsSelection: this.defaultAddons ?? [],
            defaultAddonKey: 0
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        selectedFulfillmentAccountBrand () {
            const selectedAccountGroupId = Number(this.form.fulfillment_account_group_id)
            const selectedAccountGroup = this.fulfillmentAccountGroups.find((group) => Number(group.value) === selectedAccountGroupId)

            return selectedAccountGroup?.brand ?? null
        },
        needsTermsAndConditions () {
            return this.needsTermsAndConditionsLink || this.needsTermsAndConditionsText
        },
        needsTermsAndConditionsLink () {
            if (this.form.terms_and_conditions_type === 'link') {
                return true
            }
            if (this.form.terms_and_conditions_type === 'generic_plus_link') {
                return true
            }
            if (this.form.terms_and_conditions_type === 'text_plus_link') {
                return true
            }
            if (this.form.terms_and_conditions_type === 'generic_plus_text_plus_link') {
                return true
            }

            return false
        },
        needsTermsAndConditionsText () {
            if (this.form.terms_and_conditions_type === 'text') {
                return true
            }
            if (this.form.terms_and_conditions_type === 'generic_plus_text') {
                return true
            }
            if (this.form.terms_and_conditions_type === 'text_plus_link') {
                return true
            }
            if (this.form.terms_and_conditions_type === 'generic_plus_text_plus_link') {
                return true
            }

            return false
        },
        taxonomyDivisionOptions () {
            const placeholder = [{ value: '', label: '' }]
            return [
                ...placeholder,
                ...Object.keys(this.taxonomyHierarchy).map(value => ({
                    value,
                    label: this.__(`products.taxonomy.divisions.${value}`)
                }))
            ]
        },
        availableTaxonomyCategoryOptions () {
            const division = this.form.taxonomy_product_division
            const placeholder = [{ value: '', label: '' }]
            const keys = division
                ? Object.keys(this.taxonomyHierarchy[division] ?? {})
                : []
            return [
                ...placeholder,
                ...keys.map(value => ({ value, label: this.__(`products.taxonomy.categories.${value}`) }))
            ]
        },
        availableTaxonomySubcategoryOptions () {
            const division = this.form.taxonomy_product_division
            const category = this.form.taxonomy_product_category
            const placeholder = [{ value: '', label: '' }]
            const values = (division && category) ? (this.taxonomyHierarchy[division]?.[category] ?? []) : []
            return [
                ...placeholder,
                ...values.map(value => ({ value, label: this.__(`products.taxonomy.subcategories.${value}`) }))
            ]
        }
    },

    watch: {
        form: {
            handler () {
                this.setOptionVisibility()
            },
            deep: true,
            immediate: true
        },
        'form.product_copy_id': function (newProductCopyId, oldProductCopyId) {
            if (newProductCopyId !== oldProductCopyId) {
                if (!newProductCopyId) {
                    this.form.copy_product_plans = false
                }
                this.selectProductToCopyFrom(newProductCopyId)
            }
        },
        'form.taxonomy_product_division' (newTaxonomyProductDivision) {
            const availableCategories = newTaxonomyProductDivision ? Object.keys(this.taxonomyHierarchy[newTaxonomyProductDivision] ?? {}) : []
            if (this.form.taxonomy_product_category && !availableCategories.includes(this.form.taxonomy_product_category)) {
                this.form.taxonomy_product_category = ''
                this.form.taxonomy_product_subcategory = ''
            }
        },
        'form.taxonomy_product_category' (newTaxonomyProductCategory) {
            const div = this.form.taxonomy_product_division
            const availableSubCategories = (div && newTaxonomyProductCategory) ? (this.taxonomyHierarchy[div]?.[newTaxonomyProductCategory] ?? []) : []
            if (this.form.taxonomy_product_subcategory && !availableSubCategories.includes(this.form.taxonomy_product_subcategory)) {
                this.form.taxonomy_product_subcategory = ''
            }
        }
    },
    created () {
        if (this.possibleAddonGroups.length) {
            this.getDefaultAddonOptions(this.form.addon_groups)
        }
        this.getFulfillmentModules()
    },
    methods: {
        changeFulfillmentModule () {
            this.form.fulfillment_account_group_id = null
            this.form.config_values = []
            this.configOptions = []
            this.apiOptions = {}
            this.getFulfillmentAccountGroups()
        },
        changeFulfillmentAccountGroup () {
            this.form.config_values = []
            this.configOptions = []
            this.apiOptions = {}
            this.getConfig()
        },
        getFulfillmentModules () {
            this.fulfillmentModules = []

            window.axios.get(this.$route('products.products.available-product-modules'))
                .then(response => {
                    this.fulfillmentModules.push({ label: 'None', value: null })
                    response.data.forEach((value) => this.fulfillmentModules.push({ label: value, value: value }))
                    this.getFulfillmentAccountGroups()
                })
        },
        getFulfillmentAccountGroups () {
            this.fulfillmentAccountGroups = []
            this.fulfillmentAccountGroupsError = null

            if (this.form.fulfillment_module) {
                this.isLoadingFulfillmentGroup = true

                window.axios.get(this.$route('products.products.account-groups', { module: this.form.fulfillment_module }))
                    .then(response => {
                        if (!response.data.error) {
                            this.fulfillmentAccountGroups.push({ label: 'None', value: null })
                            response.data.forEach((group) => this.fulfillmentAccountGroups.push({ label: group.name, value: group.id, brand: group.brand }))
                            this.getConfig()
                        } else {
                            this.fulfillmentAccountGroupsError = response.data.error
                        }

                        this.isLoadingFulfillmentGroup = false
                    })
            } else {
                this.configOptions = []
                this.apiOptions = {}
            }
        },
        getConfig () {
            this.configOptions = []
            this.apiOptions = {}

            if (this.form.fulfillment_module && this.form.fulfillment_account_group_id) {
                this.isLoadingFulfillmentOptions = true
                this.form.config_values = {}
                if (this.form.fulfillment_module === this.oldConfigOptions.module) {
                    const values = this.oldConfigOptions.values

                    values.forEach((row) => {
                        this.form.config_values[row.key] = {
                            key: row.key,
                            type: row.type,
                            value: row.type === 'bool' ? row.value === '1' : row.value,
                            dependencyVisibilityOptions: [],
                            visible: true
                        }
                    })
                }

                window.axios.get(this.$route('products.products.config', {
                    module: this.form.fulfillment_module,
                    fulfillmentAccountGroupId: this.form.fulfillment_account_group_id
                }))
                    .then(response => {
                        this.isLoadingFulfillmentOptions = false
                        this.configValuesSortingType = response.data.sorting_type

                        response.data.options.forEach((row) => {
                            if (!Object.prototype.hasOwnProperty.call(this.form.config_values, row.name)) {
                                this.form.config_values[row.name] = {
                                    key: row.name,
                                    type: row.type,
                                    value: row.value,
                                    dependencyVisibilityOptions: row.dependencyVisibilityOptions,
                                    visible: true
                                }
                            }

                            if (row.type === 'api') {
                                this
                                    .getOptionsFromApi(row.name, row.url)
                                    .then(() => this.configOptions.push(row))
                            } else {
                                this.configOptions.push(row)
                            }
                        })

                        this.setOptionVisibility()
                    })
            }
        },
        getOptionsFromApi (name, url) {
            return window.axios.get(url)
                .then(response => { this.apiOptions[name] = response.data })
        },
        async submitForm () {
            if (this.hasRetiredDependencies()) {
                this.showRetiredWarningModal = true
                return
            }

            await this.proceedWithSubmit()
        },
        async proceedWithSubmit () {
            this.normalizeDefaultAddons()
            await this.checkInvalidProductUpgrades()
            if (this.invalidProductUpgrades !== null) {
                this.showConfirmationModal = true
            } else {
                this.$emit('form-submitted', this.form)
            }
        },
        submitFormAfterConfirmation () {
            this.$emit('form-submitted', this.form)
        },
        normalizeDefaultAddons () {
            this.form.default_addons = this.form.default_addons.map(
                (val) => typeof val === 'object' ? val.value : val
            )
        },
        async submitFormAfterRetiredWarning () {
            this.showRetiredWarningModal = false
            await this.proceedWithSubmit()
        },
        hasRetiredDependencies () {
            this.retiredAddonNames = []
            this.retiredAddonGroupNames = []

            const selectedAddonIds = this.form.default_addons.map(
                (addon) => Number(typeof addon === 'object' ? addon.value : addon)
            )

            this.defaultAddonsSelection.forEach((addon) => {
                if (addon.retired && selectedAddonIds.includes(Number(addon.value))) {
                    this.retiredAddonNames.push(addon.text)
                }
            })

            const selectedGroupIds = this.form.addon_groups.map(Number)

            this.possibleAddonGroups.forEach((group) => {
                if (group.retired && selectedGroupIds.includes(Number(group.value))) {
                    this.retiredAddonGroupNames.push(group.text ?? group.label)
                }
            })

            return this.retiredAddonNames.length > 0 || this.retiredAddonGroupNames.length > 0
        },
        updateSelectedDowngrades (values) {
            this.form.selectedDowngrades = values
        },
        updateSelectedUpgrades (values) {
            this.form.selectedUpgrades = values
        },
        updateAutomaticPrice () {
            if (this.form.automatic_price_adjustment === false) {
                this.form.maximum_annual_price = null
            }
        },
        setOptionVisibility () {
            for (const option of this.configOptions) {
                option.visible = this.optionVisible(option)
            }
        },
        optionVisible (option) {
            const dependencies = option.dependencyVisibilityOptions

            for (const configOption of this.configOptions) {
                if (configOption.name in dependencies && this.form.config_values[configOption.name].value !== dependencies[configOption.name]) {
                    return false
                }
            }

            return true
        },
        updateSelectedAddonGroups (values) {
            this.getDefaultAddonOptions(values)
            this.form.addon_groups = values
        },
        updateSelectedDefaultAddons (values) {
            this.form.default_addons = values
        },
        updateSelectedProductClasses (values) {
            this.form.product_classes = values
        },
        updateSelectedProductSubTypes (values) {
            this.form.product_sub_types = values
        },
        selectedAddonGroupsOptions () {
            if (this.product.id) {
                const selectedGroups = this.product.product_addon_groups.map((group) => group.product_group_id)
                return this.possibleAddonGroups
                    .map((option) => ({ ...option, selected: selectedGroups.includes(option.value) }))
            }

            return this.possibleAddonGroups
        },
        getDefaultAddonOptions (addonGroups) {
            this.defaultAddonKey++
            window.axios.get(this.$route('products.addons-by-addon-groups', { addon_groups: addonGroups }))
                .then(response => {
                    if (Object.values(response.data.items).length > 0) {
                        this.defaultAddonsSelection = response.data.items.map((option) => ({
                            value: option.value,
                            text: option.label
                        }))
                    }
                })
        },
        selectedProductClassesOptions () {
            if (this.product.id && this.product.classes) {
                const selectedClasses = this.product.classes.map((productClass) => productClass.id)
                return this.productClasses
                    .map((option) => ({ ...option, selected: selectedClasses.includes(option.value) }))
            }

            return this.productClasses
        },
        selectedProductSubTypesOptions () {
            if (this.product.id && this.product.product_sub_types) {
                const selectedSubTypes = this.product.product_sub_types.map((productSubType) => productSubType.id)
                return this.subTypes
                    .map((option) => ({ ...option, selected: selectedSubTypes.includes(option.value) }))
            }

            return this.subTypes
        },
        async checkInvalidProductUpgrades () {
            if (this.form.is_create === true) {
                return
            }

            await window.axios.get(this.$route('products.invalid-product-upgrades', { product_id: this.product?.id, fulfillment_module: this.form.fulfillment_module }))
                .then(response => {
                    if (Object.values(response.data.items).length > 0) {
                        this.invalidProductUpgrades = Object.values(response.data.items).join(', ')
                    }
                })
        },
        selectProductToCopyFrom (value) {
            if (value) {
                return this.$inertia.get(this.$route('products.products.copy', { product: value }))
            }

            return this.$inertia.get(this.$route('products.products.create'))
        },
        nameRoute () {
            return this.$route('settings.tenant-translations.index')
        },
        terminationRoute () {
            return this.$route('products.termination-rules.index')
        },
        priceAdjustmentRoute () {
            return this.$route('settings.price-adjustments.index')
        },
        winbackRoute () {
            return this.$route('settings.brands.index')
        }
    }
}
</script>
<style lang="scss" scoped>
.product-form {
    display: grid;
    grid-gap: 25px;
}
.retired-warning-list {
    padding: 0 20px 15px;
    opacity: 0.6;
}
</style>
